import React, { ChangeEvent, type FC } from 'react';
import { formatFloat } from '../../utility/utility_functions';
import { LoadingSpinner } from '../icons/OurIcons';
import { useDecisionFormContext } from '../../contexts/DecisionMaking/DecisionFormContext';
import { useRepairCostContext } from '../../contexts/DecisionMaking/RepairCostContext';
import { formatNumberWithCommas } from '../../utility/utility';
import ClearButton from '../common/ClearButton';
import { FixedCostBreakdown } from '../../utility/fixedCostSettings';

// e.g. "48 yrs · $50/sqft · 1,850 sqft", or which data point is missing
export const fixedCostBreakdownSummary = (b: FixedCostBreakdown): string => {
  if (b.home_age_source === 'missing') return 'Year built is missing';
  if (b.sqft_source === 'missing') return 'Living area is missing';
  const parts = [
    b.home_age != null && b.home_age_source === 'property' ? `${b.home_age} yrs` : 'Fallback tier',
    `$${b.rate}/sqft`,
    `${Number(b.sqft).toLocaleString('en-US')} sqft${b.sqft_source === 'property' ? '' : ' (default)'}`,
  ];
  if (b.contingency_amount > 0) parts.push('+ contingency');
  if (b.bound_applied) parts.push(b.bound_applied === 'min' ? 'raised to min' : 'capped at max');
  return parts.join(' \u00b7 ');
};

const RepairCosts: FC = () => {
  const {
    state: {
      fixedCosts
    },
    dispatch,
  } = useDecisionFormContext();

  const {
    calculatedFixedCosts,
    fixedCostBreakdown,
    fixedCostsCustomized,
    isLoadingEstimatedRepairCosts,
    selectedCostSource,
    setSelectedCostSource,
    manualFixedCosts,
    setManualFixedCosts,
  } = useRepairCostContext();

  const updateFixedCosts = (value: number | null) => {
    dispatch({ type: 'SET_FORM_FIELD', payload: { field: 'fixedCosts', value: value || 0 } });
  };

  const handleSelectClick = (value: 'reix' | 'user' | null) => {
    setSelectedCostSource(value);
    updateFixedCosts(value === 'reix' ? calculatedFixedCosts : manualFixedCosts || 0);
  };

  const handleRepairInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputNumber = Number(e.target.value.replace(/,/g, '').replace('$', ''));
    if (!isNaN(inputNumber)) {
      setManualFixedCosts(inputNumber);
      setSelectedCostSource('user');
      updateFixedCosts(inputNumber);
    }
  };

  const handleClearInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setManualFixedCosts(null);
    setSelectedCostSource('reix');
    updateFixedCosts(calculatedFixedCosts);
  };

  const activeStyling = 'flex justify-center items-center border-2 rounded-xl p-3 mb-5 bg-indigo-600 bg-opacity-25 text-gray-700 hover:cursor-pointer';
  const inactiveStyling = 'flex justify-center items-center border-2 rounded-xl p-3 mb-5 hover:cursor-pointer';

  const repairCostsContent = () => {
    if (isLoadingEstimatedRepairCosts) {
      return (
        <div id='repair-costs-loading-container' role="status" className='flex items-center justify-center h-96'>
          <LoadingSpinner />
        </div>
      );
    }

    return (
      <>
        <div
          id='repair-costs-container'
          className='grid grid-cols-2 gap-x-5 text-center px-5 align-center'
        >
          <div
            id='user-fixed-costs'
            onClick={() => handleSelectClick('user')}
            className={`${selectedCostSource === 'user' ? `${activeStyling}` : `${inactiveStyling}`} flex flex-col justify-around`}
            data-autotag={`${selectedCostSource === 'user' ? 'active' : 'inactive'}-user-fixed-costs-container`}
          >
            <p className='text-center pb-2'>User Input Fixed Costs</p>
            <div id='manual-fixed-costs-container' className='flex justify-center items-center align-middle flex-wrap '>
              <input
                id='manual-fixed-costs'
                onFocus={() => handleSelectClick('user')}
                type='text'
                value={manualFixedCosts != null ? '$ ' + formatNumberWithCommas(manualFixedCosts) : ''}
                onChange={(e) => handleRepairInputChange(e)}
                className=' text-sm rounded-md border border-gray-300 shadow-x ml-3 max-w-[100px]'
              />
              <ClearButton handleClearInput={(e) => handleClearInput(e)} />
            </div>
          </div>
          <div
            id='reix-fixed-costs'
            onClick={() => handleSelectClick('reix')}
            className={`${selectedCostSource === 'reix' ? `${activeStyling}` : `${inactiveStyling}`} flex flex-col justify-around`}
            data-autotag={`${selectedCostSource === 'reix' ? 'active' : 'inactive'}-reix-fixed-costs-container`}
          >
            <p>REIX AVM Fixed Costs</p>
            <p
              data-autotag="reix-fixed-costs-value"
              className='pt-2 text-base font-bold'>${Intl.NumberFormat('en-US').format(calculatedFixedCosts)}</p>
            {!calculatedFixedCosts && <p className='text-red-500'>No fixed costs found</p>}
            {fixedCostBreakdown && (
              <p data-autotag="reix-fixed-costs-breakdown" className='pt-1 text-xs text-gray-500'>
                {fixedCostBreakdownSummary(fixedCostBreakdown)}
                {fixedCostsCustomized && <span className='block text-indigo-600'>Using your Fixed Costs settings</span>}
              </p>
            )}
          </div>
        </div>
        <div
          id='final-fixed-costs'
          className='flex justify-center items-center border-2 rounded-xl p-3 mb-5 mx-20'
        >
          <div>
            <p>Final Fixed Costs</p>
            <p id='final-fixed-costs-value' className='pt-2 text-base font-bold'>${formatFloat(fixedCosts, 2)}</p>
          </div>
        </div>
      </>);
  };

  return (
    <div className='w-contain justify-center m-3 pt-1 pb-7'>
      <div>
        <h3 className='whitespace-nowrap text-semibold text-lg text-indigo-500 text-left m-3'>Repair Costs</h3>
        <hr className='mt-3 mb-4 mx-3' />
      </div>
      {repairCostsContent()}
    </div>
  );
};

export default RepairCosts;
